import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Mediator } from '@spotahome/soyuz-mediator';

import { CHANNELS } from '../utils/constants';

import AvailabilityFilter from './components/AvailabilityFilter';
import BudgetFilter from './components/BudgetFilter';
import PropertyTypeFilter from './components/PropertyTypeFilter';

const noop = () => {};

const ExposedFilters = ({
  filters = {},
  cityId,
  currencyIsoCode,
  totalResults,
  onFiltersUpdate = noop,
  onFiltersOpen = noop,
  onFiltersClose = noop,
  onShowResultsClick = noop,
  maxPriceInCity,
  minimumStaying,
  sortingAlgorithm,
  showResults = false
}) => {
  const {
    'move-in': moveIn,
    'move-out': moveOut,
    moveOutFrom,
    moveOutTo,
    moveInFrom,
    moveInTo,
    'rentalType[]': rentalTypes,
    'type[]': propertyTypes
  } = filters;

  const [openFilter, setOpenFilter] = useState(false);

  useEffect(() => {
    const handleFiltersCloseOnEvent = () => {
      setOpenFilter(false);
    };

    Mediator.subscribe(
      CHANNELS.CLOSE_EXPOSED_FILTER,
      handleFiltersCloseOnEvent
    );

    return () => {
      Mediator.unsubscribe(
        CHANNELS.CLOSE_EXPOSED_FILTER,
        handleFiltersCloseOnEvent
      );
    };
  }, []);

  const toggleExposedFilter = filterName => () => {
    const isFilterOpen = openFilter === filterName;

    if (isFilterOpen) {
      setOpenFilter(null);
      onFiltersClose(filterName);
      Mediator.publish(CHANNELS.CLOSE_EXPOSED_FILTER);
    } else {
      setOpenFilter(filterName);
      onFiltersOpen(filterName);
      Mediator.publish(CHANNELS.OPEN_EXPOSED_FILTER);
    }
  };

  return (
    <>
      <AvailabilityFilter
        moveIn={moveIn}
        moveOut={moveOut}
        selectedRentalTypes={rentalTypes || []}
        isFilterOpen={openFilter === 'date'}
        onToggleFilter={toggleExposedFilter('date')}
        onFiltersUpdate={onFiltersUpdate}
        minimumStaying={minimumStaying}
        totalResults={totalResults}
        moveOutFrom={moveOutFrom}
        moveOutTo={moveOutTo}
        moveInFrom={moveInFrom}
        moveInTo={moveInTo}
        showResults={showResults}
        onShowResultsClick={onShowResultsClick('date')}
      />
      <PropertyTypeFilter
        selectedPropertyTypes={propertyTypes || []}
        isFilterOpen={openFilter === 'property-type'}
        onToggleFilter={toggleExposedFilter('property-type')}
        onFiltersUpdate={onFiltersUpdate}
        totalResults={totalResults}
        filters={filters}
        showResults={showResults}
      />
      <BudgetFilter
        isFilterOpen={openFilter === 'price'}
        onToggleFilter={toggleExposedFilter('price')}
        currencyIsoCode={currencyIsoCode}
        onFiltersUpdate={onFiltersUpdate}
        filters={filters}
        totalResults={totalResults}
        sortingAlgorithm={sortingAlgorithm}
        maxPriceInCity={maxPriceInCity}
        cityId={cityId}
        showResults={showResults}
      />
    </>
  );
};

ExposedFilters.propTypes = {
  totalResults: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    flexibleMoveIn: PropTypes.number,
    flexibleMoveOut: PropTypes.number,
    budget: PropTypes.string,
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    'rentalType[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string),
    'features[]': PropTypes.arrayOf(PropTypes.string),
    moveInFrom: PropTypes.string,
    moveInTo: PropTypes.string,
    moveOutFrom: PropTypes.string,
    moveOutTo: PropTypes.string
  }),
  cityId: PropTypes.string.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func,
  onFiltersOpen: PropTypes.func,
  onFiltersClose: PropTypes.func,
  maxPriceInCity: PropTypes.number.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string.isRequired,
  showResults: PropTypes.bool,
  onShowResultsClick: PropTypes.func
};

export default ExposedFilters;
